import { MenuItem, Select, Stack, Typography } from '@mui/material';
import { Provider, type Account } from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { SelectTargetAccount } from '@/components/restore/steps/selectTargetAccount';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import type { IntegrationWizardState } from '../integrationWizardFlow';

interface AmazonRedshiftConfigurationProps {
  wizardState: IntegrationWizardState;
  setWizardState: (
    updater: (state: IntegrationWizardState) => IntegrationWizardState
  ) => void;
  restoreAccountsResponse?: { accounts: Account[] };
}

export const AmazonRedshiftConfiguration = ({
  wizardState,
  setWizardState,
  restoreAccountsResponse,
}: AmazonRedshiftConfigurationProps) => {
  const dal = useDAL();
  const { vaults } = useBackupVaults();
  const [selectedClusterDatabases, setSelectedClusterDatabases] = useState<
    string[]
  >([]);
  const redshiftRegion = useMemo(() => {
    const selected = vaults.find((v) => v.id === wizardState.vaultId) || null;
    return selected?.region || '';
  }, [vaults, wizardState.vaultId]);

  const { data: clustersData } = dal.restore.redshiftClusters.useList(
    wizardState.restoreAccount?.id,
    redshiftRegion
  );
  const clusters = useMemo(() => clustersData?.clusters || [], [clustersData]);

  return (
    <>
      <Stack className='w-[582px]' gap='8px'>
        <SelectTargetAccount
          accounts={restoreAccountsResponse?.accounts}
          supportedCloudProviders={[Provider.Aws]}
          restoreAccount={wizardState.restoreAccount}
          setRestoreAccountState={(stateOrUpdater) => {
            setWizardState((s) => {
              const newState =
                typeof stateOrUpdater === 'function'
                  ? stateOrUpdater({ restoreAccount: s.restoreAccount })
                  : stateOrUpdater;
              return {
                ...s,
                restoreAccount: newState.restoreAccount,
              };
            });
          }}
          title='Select the restore account that hosts the Redshift cluster:'
          emptyMessage='No AWS restore accounts found'
          isAccountDisabled={() => false}
        />
      </Stack>
      <Stack className='w-[582px]' gap='8px'>
        <Typography>Redshift cluster</Typography>
        <Select
          value={wizardState.config?.redshift?.clusterIdentifier || ''}
          displayEmpty
          disabled={clusters.length === 0 || !redshiftRegion}
          onChange={(event) => {
            const cluster = clusters.find((c) => c.id === event.target.value);
            setWizardState((state) => ({
              ...state,
              config: {
                ...state.config,
                redshift: {
                  clusterIdentifier: cluster?.id || '',
                  database:
                    state.config?.redshift?.database &&
                    (cluster?.databases || []).includes(
                      state.config?.redshift?.database
                    )
                      ? state.config?.redshift?.database
                      : '',
                },
              },
            }));
            setSelectedClusterDatabases(cluster?.databases || []);
          }}
          renderValue={(value) => {
            if (!value) {
              if (!redshiftRegion) {
                return 'Choose a vault to determine destination region';
              }
              return clusters.length === 0
                ? 'No clusters found for the selected region'
                : 'Select a cluster';
            }
            const cluster = clusters.find((c) => c.id === value);
            return cluster
              ? cluster.name || cluster.id || 'Select a cluster'
              : 'Select a cluster';
          }}
        >
          {clusters.map((cluster) => (
            <MenuItem key={cluster.id || cluster.name} value={cluster.id || ''}>
              {cluster.name || cluster.id}
            </MenuItem>
          ))}
        </Select>
      </Stack>
      <Stack className='w-[582px]' gap='8px'>
        <Typography>Database</Typography>
        <Select
          value={wizardState.config?.redshift?.database || ''}
          displayEmpty
          disabled={selectedClusterDatabases.length === 0}
          onChange={(event) =>
            setWizardState((state) => ({
              ...state,
              config: {
                ...state.config,
                redshift: {
                  clusterIdentifier:
                    state.config?.redshift?.clusterIdentifier || '',
                  database: event.target.value || '',
                },
              },
            }))
          }
          renderValue={(value) => (value ? value : 'Select a database')}
        >
          {selectedClusterDatabases.map((db) => (
            <MenuItem key={db} value={db}>
              {db}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </>
  );
};
